import React, { useMemo, useEffect, useState } from "react";
import Form from "./Form";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowCircleRight } from "react-icons/fa";

function Hero() {
  const [offers, setOffers] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/offers.json")
      .then((res) => res.json())
      .then((data) => {
        setOffers(data);
        setIsVisible(data?.showPopup ?? false);
      })
      .catch((err) => console.error("Failed to load offers.json:", err));
  }, []);

  const animations = useMemo(
    () => ({
      badge: {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4, delay: 0.1 },
      },
      heading: {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4, delay: 0.2 },
      },
      description: {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4, delay: 0.3 },
      },
      cta: {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4, delay: 0.4 },
      },
      formCard: {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, delay: 0.3 },
      },
    }),
    []
  );

  return (
    <div className="bg-slate-50 text-slate-800 antialiased relative font-['Plus_Jakarta_Sans',sans-serif]">
      {/* Dynamic CSS styles for exact gradient text animation & 3D float keyframes */}
      <style>{`
        @keyframes shine {
          to { background-position: 200% center; }
        }
        .gradient-text-animated {
          background: linear-gradient(135deg, #059669 0%, #10B981 50%, #D97706 100%);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shine 4s linear infinite;
        }

        @keyframes float3d {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(1deg); }
        }
        .animate-float-card {
          animation: float3d 6s ease-in-out infinite;
        }

        .perspective-1000 {
          perspective: 1000px;
        }
        .preserve-3d {
          transform-style: preserve-3d;
          transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.5s ease;
        }
        .preserve-3d:hover {
          transform: translateY(-8px) rotateX(4deg) rotateY(-3deg);
        }
      `}</style>

      {/* Offers Modal Popup */}
      {isVisible && offers?.image && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-md sm:max-w-xl md:max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200">
            <button
              onClick={() => setIsVisible(false)}
              className="absolute top-3 right-3 z-50 text-white bg-slate-900/60 hover:bg-slate-900 transition-colors rounded-full w-8 h-8 flex items-center justify-center font-bold"
            >
              &times;
            </button>
            <img
              src={offers.image}
              alt="Popup Banner"
              className="w-full h-auto object-cover"
            />
            {offers.contactButton?.enabled && (
              <div className="absolute bottom-4 right-4 z-40">
                <button
                  onClick={() => {
                    setIsVisible(false);
                    navigate("/contactus");
                  }}
                  className="px-5 py-2.5 bg-emerald-600 text-white text-xs sm:text-sm font-bold rounded-xl shadow-lg hover:bg-emerald-700 transition-all"
                >
                  {offers.contactButton.label || "Contact Us"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 4. LIGHT HERO SECTION WITH 3D FORM & BACKGROUND GRAPHICS */}
      <section className="relative py-16 lg:py-24 overflow-hidden bg-gradient-to-b from-emerald-50/60 via-white to-slate-50">
        {/* Soft Ambient Background Circles */}
        <div className="absolute -top-10 -left-10 w-80 h-80 bg-emerald-200/40 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-100/60 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Copy Column */}
            <div className="lg:col-span-7 space-y-6">
              {/* Badge */}
              <motion.div {...animations.badge}>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-100/80 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-200">
                  Smart Investing Made Simple
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                {...animations.heading}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight"
              >
                Invest Smart,{" "}
                <span className="gradient-text-animated">Grow Wealth</span>,
                Secure Your Future!
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                {...animations.description}
                className="text-base sm:text-lg text-slate-600 max-w-2xl font-normal leading-relaxed"
              >
                Unlock the potential of the stock market with valuable insights and practical strategies. Whether you're a beginner or an experienced investor, take control of your financial journey and work toward long-term success.
              </motion.p>

              {/* Buttons */}
              <motion.div
                {...animations.cta}
                className="flex flex-wrap gap-4 pt-3"
              >
                <motion.div whileHover={animations.buttonHover} whileTap={animations.buttonTap}>
                  <Link
                    to="/contactus"
                    className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-green-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all"
                  >
                    Reach Us
                    <motion.span {...animations.arrow}>
                      <FaArrowCircleRight />
                    </motion.span>
                  </Link>
                </motion.div>

                <motion.div whileHover={animations.buttonHover} whileTap={animations.buttonTap}>
                  <Link
                    to="/services"
                    className="flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-emerald-600 shadow-lg hover:shadow-xl ring-1 ring-emerald-200 hover:bg-emerald-50 transition-all"
                  >
                    Our Services
                  </Link>
                </motion.div>
                </motion.div>
            </div>

            {/* Right 3D Floating Lead Form Card */}
            {/* {/* <motion.div
              {...animations.formCard}
              className="lg:col-span-5 perspective-1000"
            >
              <div className="preserve-3d animate-float-card bg-white/90 border border-slate-200/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl relative group"> */}
                {/* Top Accent Bar */}
                {/* <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-green-600 rounded-t-3xl"></div>

                <div className="flex items-center justify-between mb-6 pt-2">
                  <h3 className="text-xl font-bold text-slate-900">
                    Connect With An Expert
                  </h3>
               
                </div>  */}

                {/* Form Component */}
                <Form />
              {/* </div>
            </motion.div> */}

          </div>
        </div>
      </section>
    </div>
  );
}

export default React.memo(Hero);